import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Moon, Sun, Bell, LogOut, Hammer } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NotificationCenter } from "@/components/NotificationCenter";
import { QuickActions } from "@/components/QuickActions";
import { MobileLayout } from "@/components/mobile/MobileLayout";

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  const userEmail = user?.email || "";

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out successfully");
    navigate("/auth");
  };

  const getUserInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase();
  };

  // Mobile layout
  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  // Desktop layout
  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col w-full min-w-0">
          {/* Top Navigation */}
          <header className="sticky top-0 z-30 border-b border-border/50 glass-panel premium-shadow">
            <div className="flex h-14 md:h-16 items-center gap-2 md:gap-4 px-3 md:px-6">
              <SidebarTrigger className="hover:bg-accent/50 rounded-lg transition-colors" />
              
              <div className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1.5 md:py-2 rounded-xl bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border border-primary/20">
                <div className="p-1 md:p-1.5 rounded-lg bg-gradient-to-br from-primary to-primary-glow">
                  <Hammer className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <span className="hidden sm:inline font-bold text-base md:text-lg bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  RoofGenius AI
                </span>
              </div>

              <div className="flex-1" />

              {/* Right Side Actions */}
              <div className="flex items-center gap-1 md:gap-2">
                <QuickActions />
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="h-9 w-9 md:h-10 md:w-10 rounded-full hover:bg-accent/50 transition-all hover:scale-105"
                >
                  {theme === "light" ? (
                    <Moon className="w-4 h-4 md:w-5 md:h-5 text-neon-purple" />
                  ) : (
                    <Sun className="w-4 h-4 md:w-5 md:h-5 text-neon-orange" />
                  )}
                </Button>

                <NotificationCenter />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full h-9 w-9 md:h-10 md:w-10 p-0 transition-all hover:scale-105 hover:premium-shadow">
                      <Avatar className="h-9 w-9 md:h-10 md:w-10 ring-2 ring-primary/20 ring-offset-2 ring-offset-background">
                        <AvatarFallback className="bg-gradient-to-br from-primary to-primary-glow text-primary-foreground font-bold text-xs md:text-sm">
                          {getUserInitials(userEmail)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Account</span>
                        <span className="text-xs text-muted-foreground">{userEmail}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/settings")}>
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-auto bg-gradient-to-br from-background via-background to-muted/20">
            <div className="w-full max-w-[1600px] mx-auto p-3 md:p-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
